import Box from 'v2/components/UI/Box';
import Text from 'v2/components/UI/Text';
import InputText from 'v2/components/Inputs/InputText';
import Select from 'v2/components/Select';
import useUser from './hooks/useUser';
import { formatCpfAndCnpj } from 'v2/resources/utils/formatCpfAndCpnj';
import formatPhoneView from 'v2/resources/utils/formatPhone';
import Switch from 'v2/components/Switch';
import Checkbox from 'v2/components/Checkbox';
import { IItemCheckBox, IPermission } from './types';
import ButtonCta from 'v2/components/ButtonCta';
import ButtonBase from 'v2/components/UI/ButtonBase';

const User: React.FC = () => {
  const {
    optionsNature,
    typeNature,
    formik,
    listPermissions,
    isLoadingNew,
    isLoadingEdit,
    user,
    title,
    isMobile,
    isTablet,
    handleTypeNature,
    handleCheckbox,
    handleCheckSwitch,
    handleSubmit,
    handleCheckSwitchActive,
  } = useUser();

  const renderListPermissions = (permission: IPermission, length: number) => {
    if (permission.items.length > length) {
      return (
        <Box width="100%" justifyContent="space-between" gap={10}>
          {[0, 1].map((colIdx) => {
            const half = Math.ceil(permission.items.length / 2);
            const itemsSlice = permission.items.slice(
              colIdx === 0 ? 0 : half,
              colIdx === 0 ? half : permission.items.length
            );

            return (
              <Box
                width="100%"
                maxWidth={'48%'}
                flexDirection="column"
                gap={20}
              >
                {itemsSlice.map((item) => {
                  const value: IItemCheckBox = {
                    value: permission.value,
                    item: item.value,
                  };
                  return (
                    <Checkbox
                      key={item.value}
                      checked={formik.values.permissions[
                        permission.value
                      ].includes(item.value)}
                      onChange={handleCheckbox}
                      title={item.label}
                      value={JSON.stringify(value)}
                      size={16}
                    />
                  );
                })}
              </Box>
            );
          })}
        </Box>
      );
    }
  };

  const renderButtons = () => {
    return (
      <Box width={'100%'} justifyContent="center" alignItems="center" gap={20}>
        <ButtonBase
          onClick={() => {}}
          padding={'15px 30px'}
          border={'2px solid #005F47'}
          borderRadius={12}
          color="#005F47"
          fontWeight={700}
          fontSize={16}
          width={'100%'}
          maxWidth={220}
          justifyContent="center"
        >
          Cancelar
        </ButtonBase>
        <ButtonCta
          title="Salvar"
          handleClick={handleSubmit}
          padding={'15px 30px'}
          width={'100%'}
          maxWidth={250}
          disabled={!formik.isValid || isLoadingNew || isLoadingEdit}
          isLoading={isLoadingNew || isLoadingEdit}
        />
      </Box>
    );
  };

  const renderHeaderSwitchEnable = () => {
    if (user) {
      return (
        <Box
          background={'#fff'}
          borderRadius={12}
          padding={20}
          width={'100%'}
          flexDirection="column"
          gap={10}
        >
          <Text fontSize={18} fontWeight={700} color="#005F47">
            Esse usuário está ativo na plataforma?
          </Text>

          <Box alignItems="center" gap={10}>
            <Text fontSize={16} fontWeight={500} color="#1b1b1b">
              Não
            </Text>
            <Switch
              name="active"
              value="active"
              onChange={handleCheckSwitchActive}
              checked={!!formik.values.active}
            />
            <Text fontSize={16} fontWeight={500} color="#1b1b1b">
              Sim
            </Text>
          </Box>
        </Box>
      );
    }
  };
  return (
    <Box flexDirection="column" width={'100%'} gap={30} padding={'30px 20px'}>
      <Box alignItems="center" justifyContent="space-between" width={'100%'}>
        <Text fontSize={24} fontWeight={700} color="#1B1B1B">
          {title}
        </Text>
      </Box>

      {renderHeaderSwitchEnable()}
      <Box flexDirection="column" gap={10}>
        <Text fontSize={18} fontWeight={700} color="#005F47">
          Dados pessoais do usuário
        </Text>

        <Box
          background={'#fff'}
          borderRadius={12}
          padding={20}
          width={'100%'}
          flexDirection="column"
          gap={10}
        >
          <Box
            width={'100%'}
            gap={10}
            justifyContent={isMobile ? 'center' : 'space-between'}
            alignItems="center"
            flexWrap="wrap"
          >
            <Select
              backgroundColor="#f7f6f9"
              options={optionsNature}
              value={typeNature}
              labelId="options-label"
              selectId="options-id"
              label="Natureza"
              onChange={handleTypeNature}
              id="nature"
              width={'100%'}
              maxWidth={isMobile ? '100%' : '32%'}
              borderRadius={'10px'}
            />

            <InputText
              placeholder="Nome completo/Razão social: *"
              maxWidth={isMobile ? '100%' : '32%'}
              value={formik.values.name}
              onChange={formik.handleChange('name')}
              errorMessage={formik.errors.name}
            />

            <InputText
              placeholder="CPF/CNPJ: *"
              maxWidth={isMobile ? '100%' : '32%'}
              value={formatCpfAndCnpj(formik.values.cpfCnpj)}
              onChange={formik.handleChange('cpfCnpj')}
              errorMessage={formik.errors.cpfCnpj}
            />
          </Box>

          <Box
            width={'100%'}
            gap={10}
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
          >
            <InputText
              placeholder="Celular: *"
              maxWidth={isMobile ? '100%' : '32%'}
              value={formatPhoneView(formik.values.cellphone)}
              onChange={formik.handleChange('cellphone')}
              errorMessage={formik.errors.cellphone}
            />
            <InputText
              placeholder="E-mail: *"
              maxWidth={isMobile ? '100%' : '32%'}
              value={formatPhoneView(formik.values.email)}
              onChange={formik.handleChange('email')}
              errorMessage={formik.errors.email}
            />
            <InputText
              placeholder="Função: *"
              maxWidth={isMobile ? '100%' : '32%'}
              value={formatPhoneView(formik.values.function)}
              onChange={formik.handleChange('function')}
              errorMessage={formik.errors.function}
            />
          </Box>
        </Box>
      </Box>

      <Box flexDirection="column" gap={10}>
        <Text fontSize={18} fontWeight={700} color="#005F47">
          Permissões de acesso
        </Text>

        <Box
          background={'#fff'}
          borderRadius={12}
          padding={20}
          width={'100%'}
          justifyContent={isTablet || isMobile ? 'center' : 'space-between'}
          flexWrap="wrap"
          gap={10}
        >
          {listPermissions.map((permission, index) => {
            const length = permission.items.length / 2;
            return (
              <Box
                key={index}
                background={'#F7F6F9'}
                borderRadius={12}
                padding={20}
                width={'100%'}
                maxWidth={isTablet || isMobile ? '100%' : '48%'}
                flexDirection="column"
                gap={10}
              >
                <Box alignItems="center" justifyContent="space-between">
                  <Text fontSize={16} fontWeight={700} color="#005F47">
                    {permission.label}
                  </Text>

                  <Switch
                    name="performance"
                    value={permission.value}
                    onChange={handleCheckSwitch}
                    checked={
                      permission.items.length ===
                      formik.values.permissions[permission.value].length
                    }
                  />
                </Box>

                <Box height={1} background={'#DEDEDE'} width={'100%'} />

                {renderListPermissions(permission, length)}
              </Box>
            );
          })}
        </Box>
      </Box>

      {renderButtons()}
    </Box>
  );
};

export default User;
